import { FormEvent, useState } from 'react';
import { strings } from '@/i18n/strings';
import { Button } from '@components/__shared/Button/Button';
import { Dialog } from '@components/__shared/Dialog/Dialog';
import { StarRating } from '@components/__shared/StarRating/StarRating';
import styles from './RecommendationDialog.module.scss';

interface Props {
	onClose: () => void;
}

// Submissions are collected by Netlify Forms (dashboard -> Forms -> "reference").
// The build-time detection form lives in index.html; this posts to it.
export const FORM_NAME = 'reference';

type SubmitState = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'done' } | { kind: 'error'; message: string };

export const RecommendationDialog = ({ onClose }: Props) => {
	const [quote, setQuote] = useState('');
	const [name, setName] = useState('');
	const [rating, setRating] = useState(0);
	const [botField, setBotField] = useState('');
	const [state, setState] = useState<SubmitState>({ kind: 'idle' });

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (rating === 0) {
			setState({ kind: 'error', message: strings.recommendationDialog.ratingRequired });
			return;
		}

		setState({ kind: 'submitting' });

		try {
			const res = await fetch('/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: new URLSearchParams({
					'form-name': FORM_NAME,
					name,
					quote,
					rating: String(rating),
					'bot-field': botField,
				}).toString(),
			});

			if (!res.ok) {
				setState({ kind: 'error', message: strings.recommendationDialog.genericError });
				return;
			}

			setState({ kind: 'done' });
		} catch {
			setState({ kind: 'error', message: strings.recommendationDialog.networkError });
		}
	};

	if (state.kind === 'done') {
		return (
			<Dialog title={strings.recommendationDialog.thankYouTitle} onClose={onClose}>
				<p className={styles.thanks}>{strings.recommendationDialog.thankYouBody}</p>
				<Button onClick={onClose}>{strings.recommendationDialog.close}</Button>
			</Dialog>
		);
	}

	return (
		<Dialog title={strings.recommendationDialog.title} onClose={onClose}>
			<form
				className={styles.form}
				name={FORM_NAME}
				method="POST"
				data-netlify="true"
				netlify-honeypot="bot-field"
				onSubmit={handleSubmit}
			>
				<input type="hidden" name="form-name" value={FORM_NAME} />

				<label className={styles.field}>
					<span>{strings.recommendationDialog.experienceLabel}</span>
					<textarea
						name="quote"
						value={quote}
						onChange={e => setQuote(e.target.value)}
						rows={4}
						maxLength={500}
						required
					/>
				</label>

				<label className={styles.field}>
					<span>{strings.recommendationDialog.nameLabel}</span>
					<input
						type="text"
						name="name"
						value={name}
						onChange={e => setName(e.target.value)}
						maxLength={100}
						required
					/>
				</label>

				<div className={styles.field}>
					<span>{strings.recommendationDialog.ratingLabel}</span>
					<StarRating value={rating} onChange={setRating} />
					<input type="hidden" name="rating" value={rating} />
				</div>

				<label className={styles.honeypot} aria-hidden="true">
					<span>{strings.recommendationDialog.honeypotLabel}</span>
					<input
						type="text"
						name="bot-field"
						tabIndex={-1}
						autoComplete="off"
						value={botField}
						onChange={e => setBotField(e.target.value)}
					/>
				</label>

				{state.kind === 'error' && (
					<p className={styles.error} role="alert">
						{state.message}
					</p>
				)}

				<p className={styles.note}>{strings.recommendationDialog.reviewNote}</p>

				<Button type="submit" disabled={state.kind === 'submitting'}>
					{state.kind === 'submitting'
						? strings.recommendationDialog.submitting
						: strings.recommendationDialog.submit}
				</Button>
			</form>
		</Dialog>
	);
};
